import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderKanban, Users, Star, MessageSquare, Clock, PlusCircle, Eye, Settings } from 'lucide-react';
import { getData } from '../services/storage';
import { StatCard } from '../components/Cards';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const portfolio = getData('portfolio') || [];
    const leads = getData('leads') || [];
    const media = getData('media') || [];
    const testimonials = getData('content')?.testimonials || [];

    const categories = [...new Set(portfolio.map((p) => p.category).filter(Boolean))];

    setStats({
      totalPortfolio: portfolio.length,
      totalCategories: categories.length,
      totalTestimonials: Array.isArray(testimonials) ? testimonials.length : 0,
      totalLeads: leads.length,
      totalMedia: media.length,
      featuredPortfolio: portfolio.filter((p) => p.featured).length,
    });

    setLastUpdated(new Date().toLocaleString());
    setLoading(false);

    console.log('[Dashboard] Stats computed:', {
      portfolio: portfolio.length,
      categories: categories.length,
      leads: leads.length,
      testimonials: Array.isArray(testimonials) ? testimonials.length : 0,
    });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500 mx-auto mb-3"></div>
          <p className="text-slate-500 text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const statCards = [
    { icon: FolderKanban, label: 'Total Portfolio Projects', value: stats?.totalPortfolio || 0, color: 'purple' },
    { icon: Star, label: 'Total Categories', value: stats?.totalCategories || 0, color: 'amber' },
    { icon: Star, label: 'Total Testimonials', value: stats?.totalTestimonials || 0, color: 'yellow' },
    { icon: MessageSquare, label: 'Contact Requests', value: stats?.totalLeads || 0, color: 'blue' },
  ];

  const quickActions = [
    { label: 'Add Portfolio', icon: PlusCircle, color: 'purple', onClick: () => navigate('/admin/portfolio') },
    { label: 'View Portfolio', icon: Eye, color: 'indigo', onClick: () => window.open('/', '_blank') },
    { label: 'Contact Settings', icon: Settings, color: 'pink', onClick: () => navigate('/admin/contact-settings') },
    { label: 'Media Manager', icon: FolderKanban, color: 'emerald', onClick: () => navigate('/admin/media') },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">Overview</h2>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Clock size={14} />
          <span>Last updated: {lastUpdated}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {statCards.map((card, i) => (
          <StatCard key={i} {...card} />
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-base font-semibold text-slate-800 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {quickActions.map((action, i) => {
            const iconBg = {
              purple: 'bg-purple-100 text-purple-600',
              indigo: 'bg-indigo-100 text-indigo-600',
              pink: 'bg-pink-100 text-pink-600',
              emerald: 'bg-emerald-100 text-emerald-600',
            };
            const border = {
              purple: 'hover:border-purple-300',
              indigo: 'hover:border-indigo-300',
              pink: 'hover:border-pink-300',
              emerald: 'hover:border-emerald-300',
            };
            const ibg = iconBg[action.color] || iconBg.purple;
            const bdr = border[action.color] || border.purple;
            return (
            <button
              key={i}
              onClick={action.onClick}
              className={`flex items-center gap-3 p-4 rounded-xl border border-slate-200 hover:shadow-md transition-all text-left bg-white ${bdr}`}
            >
              <div className={`p-2.5 rounded-lg ${ibg}`}>
                <action.icon size={20} />
              </div>
              <span className="text-sm font-medium text-slate-700">{action.label}</span>
            </button>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-base font-semibold text-slate-800 mb-4">Portfolio Summary</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
              <span className="text-sm text-slate-700">Total Items</span>
              <span className="text-lg font-bold text-purple-600">{stats?.totalPortfolio}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
              <span className="text-sm text-slate-700">Categories</span>
              <span className="text-lg font-bold text-amber-600">{stats?.totalCategories}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
              <span className="text-sm text-slate-700">Featured Projects</span>
              <span className="text-lg font-bold text-emerald-600">{stats?.featuredPortfolio}</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-base font-semibold text-slate-800 mb-4">Leads Overview</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <span className="text-sm text-slate-700">Total Enquiries</span>
              <span className="text-lg font-bold text-blue-600">{stats?.totalLeads}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
              <span className="text-sm text-slate-700">Testimonials</span>
              <span className="text-lg font-bold text-yellow-600">{stats?.totalTestimonials}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-700">Media Files</span>
              <span className="text-lg font-bold text-slate-600">{stats?.totalMedia}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
